import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bell, BookOpen, Home, LogOut, Menu as MoreIcon, Search, Store } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import type { ChannelSummary } from "../../lib/types";
import { Drawer } from "../ui/Drawer";
import { useNavGroups } from "../../hooks/useNavGroups";

/**
 * Tab bar inferior do mobile — substitui o menu lateral, que nao aparece
 * mais nesse breakpoint (handoff, comportamento §mobile). "Mais" abre uma
 * folha com TODAS as telas agrupadas, reaproveitando useNavGroups (mesma
 * fonte de verdade do Nav desktop, pra nunca divergir sobre o que existe).
 */
export function TabBar(): JSX.Element {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, hasAccessToken, user, logout } = useAuth();
  const groups = useNavGroups();

  const { data: channels = [] } = useQuery<ChannelSummary[]>({
    queryKey: ["channels"],
    queryFn: async () => {
      const r = await api.get<ChannelSummary[]>("/api/channels");
      return r.data;
    },
    enabled: isAuthenticated && hasAccessToken,
  });
  const firstChannel = channels[0];
  // Mesma fonte de verdade do Nav desktop: o item so existe em useNavGroups
  // quando o papel pode ve-lo (BUG-01 do QA: operador comum caia num
  // AdminGuard e voltava pro Painel em silencio). Sem permissao, a 4a aba
  // vira o Manual, que todo papel acessa.
  const canSeeNotifications = groups.some((g) =>
    g.items.some((i) => i.to === "/admin/notificacoes"),
  );

  return (
    <>
      <nav className="tabbar" aria-label="Navegação (mobile)">
        <NavLink to="/" end className="tabbar__item">
          <Home className="icon" aria-hidden />
          <span>Início</span>
        </NavLink>
        <NavLink
          to={firstChannel ? `/canais/${firstChannel.slug}` : "/"}
          className="tabbar__item"
          aria-current={location.pathname.startsWith("/canais") ? "page" : undefined}
        >
          <Store className="icon" aria-hidden />
          <span>Canal</span>
        </NavLink>
        <button
          type="button"
          className="tabbar__item"
          onClick={() => navigate("/buscar")}
          aria-current={location.pathname.startsWith("/buscar") ? "page" : undefined}
        >
          <Search className="icon" aria-hidden />
          <span>Buscar</span>
        </button>
        {canSeeNotifications ? (
          <NavLink to="/admin/notificacoes" className="tabbar__item">
            <Bell className="icon" aria-hidden />
            <span>Avisos</span>
          </NavLink>
        ) : (
          <NavLink to="/manual" className="tabbar__item">
            <BookOpen className="icon" aria-hidden />
            <span>Manual</span>
          </NavLink>
        )}
        <button
          type="button"
          className="tabbar__item"
          onClick={() => setMoreOpen(true)}
          aria-expanded={moreOpen}
        >
          <MoreIcon className="icon" aria-hidden />
          <span>Mais</span>
        </button>
      </nav>

      <Drawer open={moreOpen} onOpenChange={setMoreOpen} side="bottom" title="Todas as telas">
        <div className="stack">
          {groups.map((group) => (
            <div key={group.title} className="stack" style={{ gap: ".25rem" }}>
              <div className="filters__group-label">{group.title}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMoreOpen(false)}
                  className="row"
                  style={{ gridTemplateColumns: "1.5rem 1fr", padding: ".5rem", borderRadius: "var(--radius-md)" }}
                >
                  {item.icon}
                  <span className="row__title" style={{ fontSize: "var(--text-md)" }}>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
          {/* Conta: o "Sair" vivia so no header desktop; abaixo de 64rem o
              header mobile nao o mostra e o celular ficava sem como encerrar
              a sessao (QA da branch fix/redesign-tokens-e-vocabulario). */}
          <div className="stack" style={{ gap: ".25rem" }}>
            <div className="filters__group-label">Conta</div>
            {user?.email ? (
              <div className="row__sub" style={{ padding: "0 .5rem" }}>{user.email}</div>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setMoreOpen(false);
                void logout();
              }}
              className="row"
              style={{ gridTemplateColumns: "1.5rem 1fr", padding: ".5rem", borderRadius: "var(--radius-md)", width: "100%", textAlign: "left", background: "transparent", border: "none", font: "inherit", color: "var(--color-ink)", cursor: "pointer" }}
            >
              <LogOut className="icon" aria-hidden />
              <span className="row__title" style={{ fontSize: "var(--text-md)" }}>Sair</span>
            </button>
          </div>
        </div>
      </Drawer>
    </>
  );
}
